/**
 * PayPal pay button — renders the PayPal JS SDK button via
 * `@paypal/react-paypal-js`.
 *
 * Card funding is disabled because credit-card processing must remain
 * on QuickBooks.  Pay Later + Venmo are enabled — the SDK auto-detects
 * customer eligibility and reports it via `onInit` / `isFundingEligible`.
 */
import { useMemo } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { shopPaymentsAPI } from '@/lib/api';

export default function PayPalPayButton({
  paypalConfig,      // { client_id, environment, currency }
  method,            // "paypal" | "paypal_paylater"
  onApproved,        // async ({ order_id, provider_payment_id }) => void
  onError,           // (err) => void
  disabled,
  createOrderFn,     // optional async () => { paypal_order_id, order_id } (special orders)
}) {
  // useMemo so the SDK doesn't re-load on every parent re-render.
  const scriptOptions = useMemo(() => ({
    'client-id': paypalConfig?.client_id,
    currency: paypalConfig?.currency || 'USD',
    components: 'buttons,funding-eligibility',
    'enable-funding': 'paylater,venmo',
    'disable-funding': 'card', // card stays on QuickBooks
    intent: 'capture',
    // Sandbox vs live is determined server-side by which client_id is used.
  }), [paypalConfig?.client_id, paypalConfig?.currency]);

  if (!paypalConfig?.client_id) return null;

  return (
    <div data-testid={`paypal-btn-${method}`} className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      <PayPalScriptProvider options={scriptOptions}>
        <PayPalButtons
          fundingSource={method === 'paypal_paylater' ? 'paylater' : 'paypal'}
          style={{
            // `label` must stay a valid SDK value.  For Pay Later we
            // just switch `fundingSource` — the SDK auto-renders the
            // "Pay Later" wordmark; passing `label='paylater'` is
            // rejected as invalid by the SDK.
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal',
            height: 44,
          }}
          disabled={disabled}
          createOrder={async () => {
            const r = createOrderFn
              ? { data: await createOrderFn() }
              : await shopPaymentsAPI.paypalCreateOrder({ method });
            // Stash the KR order id on the SDK payload for the capture step.
            window.__kr_pending_paypal_order = {
              paypal_order_id: r.data.paypal_order_id,
              kr_order_id: r.data.order_id,
            };
            return r.data.paypal_order_id;
          }}
          onApprove={async (data) => {
            const pending = window.__kr_pending_paypal_order || {};
            const kr_order_id = pending.kr_order_id;
            const r = await shopPaymentsAPI.paypalCapture({
              paypal_order_id: data.orderID,
              kr_order_id,
            });
            window.__kr_pending_paypal_order = null;
            onApproved && await onApproved(r.data);
          }}
          onError={(err) => { onError && onError(err); }}
          onCancel={() => { window.__kr_pending_paypal_order = null; }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
